<script lang="ts" setup>
const { isMobile } = useDevice();
import { useGlobalStore } from "~/stores/globalStore";
const globalStore = useGlobalStore();
const config = useRuntimeConfig();
const baseURL = config.public.baseUrl;

const {data} = await useFetch(
  `${baseURL}/content/services/development-services/aws-cloud-and-migration-services-content.json`
);

useHead({
  title: data?.meta_tags?.title || "",
  meta: data?.meta_tags?.meta || [],
});
const visible = ref(false);

const openModal = () => {
  visible.value = true;
};
</script>

<template>
  <!-- Section |  AWS Cloud and Migration  -->
  <section class="px-4 mx-auto md:mt-10">
    <div
      class="flex max-md:flex-col justify-between gap-10 max-w-screen-xl mx-auto"
    >
      <div class="md:w-[55%]">
        <h2 class="mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data?.header.heading"
            append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p v-if="data?.header?.text" class="lg:text-xl tracking-[0.5px]">
          {{ data?.header.text }}
        </p>
        <p class="lg:text-xl my-6">Excited! Talk to our experts.</p>
        <WrButton @click="openModal">Get a Quote</WrButton>
      </div>
      <div>
        <NuxtImg
          :src="data?.header.image"
          :alt="data?.header.heading"
          width="497"
          height="429"
          class="min-w-max"
          loading="lazy"
          format="webp"
        />
      </div>
    </div>
  </section>
  <!-- Section |  What AWS means:  -->
  <section class="dark:bg-none py-20 lg:py-36">
    <WrVideoHeroSection :data="data?.our_promises" />
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center my-6">
        <WrGradientText>
          {{ data?.migrate_to_aws?.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data?.migrate_to_aws?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <div v-for="service in data?.migrate_to_aws?.expertisetext">
          <ServiceValueCard
            :img="service?.image"
            :title="service?.heading"
            :description="service?.text"
          />
        </div>
      </div>
    </div>
  </section>
  <!-- Section | Moving to the AWS Cloud -->
  <section class="dark:bg-transparent">
    <div class="max-w-screen-xl mx-auto py-20 px-4 lg:px-8">
      <WrCardCTA
        append-class="max-md:px-4 py-8"
        :description="data?.project_in_mind?.subheading"
        :icon="data?.project_in_mind?.image"
        :title="data?.project_in_mind?.heading"
        :subtitle="data?.project_in_mind?.small_heading"
        :button-text="data?.project_in_mind?.linkText"
      />
    </div>
  </section>

  <!-- Section | Advantages of AWS  -->
     <section class="px-4 xl:mt-20 mt-16 mb-16 relative z-10">
    <div
      class="max-w-screen-xl mx-auto m-4 lg:grid lg:grid-cols-[2fr_3fr] lg:gap-4"
    >
      <WrFeatureGridSection :data="data?.build_team" @open-modal="openModal" />
    </div>
  </section>

  <WrOnboarding :onboarding="data?.our_services?.services" />

  <section class="max-w-screen-xl px-4 mx-auto mt-20">
    <WrImageListSection :data="data?.development_team_content" />
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center my-6">
        <WrGradientText>
          {{ data?.aws_benefits?.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data?.aws_benefits?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <div v-for="service in data?.aws_benefits?.benefit_items">
          <ServiceValueCard
            :img="service?.image"
            :title="service?.heading ?? service?.title"
            :description="service?.text"
          />
        </div>
      </div>
    </div>
  </section>

  <section class="max-w-screen-xl px-4 mx-auto mt-20">
    <WrImageListSection :data="data?.migration_content" />
  </section>
  <section class="max-w-screen-xl px-4 mx-auto mt-20">
    <div class="grid sm:grid-cols-2 gap-14 lg:gap-44 lg:px-12 mt-12">
      <WrCardCTA
        v-for="card in data?.contact_us_carousel?.boxes"
        :img-position="card.imgPosition"
        append-class="pt-36 max-lg:pb-8 lg:py-14 lg:pl-20 pr-8"
        :icon="card.image"
        alt="Development Services"
        :subtitle="card?.small_heading"
        :title="card?.heading"
        :description="card?.subheading"
        :buttonText="card?.link_text"
        :icon-append-class="card?.iconAppend"
      />
    </div>
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center">
        <WrGradientText>
          {{ data.development_process.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px] mb-10"
      >
        {{ data.development_process.subheading }}
      </p>
      <!--Section | Our e-commerce solutions to operational needs. Starts-->
      <WrStepper
        :steps="data.development_process.steps"
        :layout="isMobile ? 'mobile' : 'desktop'"
      />
    </div>
  </section>

  <section class="px-4 relative z-10 dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl md:px-4 mx-auto">
      <h3 class="font-bold text-center leading-tight relative xl:px-0 lg:px-20">
        <WrGradientText>
          {{ data.technology_stack.heading }}
        </WrGradientText>
      </h3>
      <p class="xl:mt-4 text-base text-center max-md:px-0 !leading-[1.8]">
        {{ data.technology_stack.subheading }}
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <CardTechnologyStack
          v-for="stac in data.technology_stack.stacks"
          :img="stac.image"
          :title="stac.title"
          :stacs="stac.list"
          :layoutChange="true"
        />
      </div>
    </div>
  </section>

  <section>
    <div
      class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10 pb-10 lg:pb-24 mt-20"
    >
      <div
        v-if="$colorMode.preference != 'dark'"
        class="size-[322px] absolute top-40 left-10 rounded-full blur-[150px] bg-[linear-gradient(180deg,_rgba(73,2,166,0.2)_0%,_rgba(255,164,235,0.2)_100%)]"
      ></div>
      <div class="max-md:text-center mx-auto max-w-[504px] z-10 relative">
        <div class="text-3xl lg:text-[48px]">
          <WrGradientText
            :text="data?.faq?.heading"
            append-class="py-0 md:py-4 leading-[1]"
          />
        </div>
        <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
          {{ data?.faq?.subheading }}
        </p>
      </div>
      <div class="grow">
        <WrAccordion :Content="data?.faq?.collapses" />
      </div>
    </div>
  </section>

  <section class="max-w-screen-xl mx-auto px-4 py-6">
    <h2 class="lg:text-center">
      <WrGradientText append-class="leading-[1]">
        {{ data?.other_services?.side_box?.heading }}
      </WrGradientText>
    </h2>
    <p
      class="max-w-screen-md mx-auto lg:text-lg lg:text-center tracking-[0.5px] mb-5 mt-2"
    >
      {{ data?.other_services?.side_box?.subheading }}
    </p>
    <WrFrameworksCarousel />
  </section>

  <section
    class="relative py-10 lg:py-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
  >
    <h2 class="text-center">
      <WrGradientText text="Trusted for excellence and efficiency" />
    </h2>
    <p
      class="text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
    >
      Webreinvent is trusted by leading organisations to enhance their
      development and operational efficiency.
    </p>
    <!-- Carousel Section -->

    <WrTestimonialCardCarousel />

    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-2"
      >View All</WrButton
    >
  </section>

  <section class="max-w-screen-xl px-4 mx-auto mt-20">
    <WrImageListSection :data="data?.migration_content" />
  </section>
  <section class="max-w-screen-xl px-4 mx-auto mt-20">
    <div class="text-center">
      <h2 class="mb-2 max-md:mb-4 md:mb-4">
        <WrGradientText
          :text="data?.contact_us_bar2?.heading"
          append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
        />
      </h2>
      <p class="lg:text-xl tracking-[0.5px]">
        {{ data?.contact_us_bar2?.subheading }}
      </p>
    </div>
    <div class="grid sm:grid-cols-2 gap-14 lg:gap-44 lg:px-12 mt-12">
      <WrCardCTA
        v-for="card in data?.call_to_action?.boxes"
        :img-position="card.imgPosition"
        append-class="pt-36 max-lg:pb-8 lg:py-14 lg:pl-20 pr-8"
        :icon="card.image"
        alt="Development Services"
        :subtitle="card?.small_heading"
        :title="card?.heading"
        :description="card?.subheading"
        :buttonText="card?.linkText"
        :icon-append-class="card?.iconAppend"
      />
    </div>
  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
  </section>
</template>
